import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Multiselect from '@cloudscape-design/components/multiselect';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Table from '@cloudscape-design/components/table';
import Toggle from '@cloudscape-design/components/toggle';
import { useNavigate } from 'react-router';
import {
  ALL_CURRENCIES,
  ALL_PAYMENT_MODES,
  pspModeEnabled,
  type Currency,
  type PspConfig,
} from '@etiquetachef/types';
import { subscribePspConfigs, type PspConfigListResult } from '@/lib/firestoreTenantDetail';
import { setPspConfigCallable, deletePspConfigCallable } from '@/lib/callables';
import { toast } from '@/lib/notifications';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { CODE_DEFAULTS, CODE_INTEGRATED } from './pspDefaults';
import { flagEmoji } from '@/features/countries/worldCountries';

/** Linha da tabela: um doc do catálogo, ou um PSP integrado ainda fora dele. */
type PspRow = PspConfig & { readonly virtual?: boolean };

type EditorState =
  | { mode: 'closed' }
  | { mode: 'create' }
  | { mode: 'edit'; current: PspConfig };

/**
 * `/psp` — lista dos PSPs: o catálogo aberto `/config/psps` mais os integrados
 * no código que ainda não foram cadastrados, com o status de PRODUÇÃO e de
 * SANDBOX. "Configurar" leva ao editor (`/psp/:id`, `PspEditorPage`), onde
 * ficam os modos, as credenciais e o teste de conexão. "Adicionar PSP" cria um
 * PSP só de catálogo; apagar é hard-delete só sem assinatura ativa (o servidor
 * barra com `psp_has_active_subscriptions`).
 *
 * Origem: sem issue — commit 2b485856b, 2026-06-11 14:39 +0200 (Sprint 40, aba PSP de `/countries`);
 * virou página na #756 — 2026-10-01 13:31 +0200 (owner: "todo setup PSP deve
 * ficar em /psp").
 *
 * Usado por: rota `psp` (`routes/router.tsx`), menu Sistema (`AdminLayout`).
 *
 * `CODE_INTEGRATED` são os PSPs com cobrança real no código. Catálogo ≠
 * cobrança: um PSP só cobra com `<psp>.ts` + secrets + registry.
 *
 * Contrato: assina `config/psps/items` (`subscribePspConfigs`); toda escrita
 * por callable (`setPspConfig`, `deletePspConfig`), com motivo.
 *
 * Arquitetura: `match /config/psps/items/{pspId}` nega escrita do cliente;
 * Asaas só no BR e Stripe universal são regras do servidor
 * (`setMarketConfig`), não desta lista. Spec: `docs/screens/psp-admin.md`.
 *
 * Histórico:
 *  - 2026-10-25 15:00 +0100 — (#1021) a recusa da exclusão (ex.:
 *    `psp_has_active_subscriptions`) sai num `Alert` dentro do confirm, que fica
 *    aberto — o toast ficava atrás do overlay (mesmo achado do CRM, #174).
 *  - 2026-10-10 13:45 +0200 — (#926) esta docstring estava acima de `type PspRow`
 *    (o gate não a ligava à tela); movida para cá sem mudar o texto.
 *  - 2026-10-01 13:31 +0200 — (#756) sai de `/countries` para `/psp`; a coluna
 *    de status vira Produção/Sandbox e "Editar" leva ao editor em rota.
 *  - 2026-10-01 13:20 +0200 — (#756) `CODE_INTEGRATED` era um set à mão com só
 *    `stripe`, e o Asaas (registrado no backend desde a A2, #751) não aparecia
 *    — nem no catálogo, onde nunca foi cadastrado. Agora vem de
 *    `MARKET_SELECTABLE_PROVIDERS` (`@etiquetachef/types`, o mesmo do seletor
 *    de PSP do país), e PSP integrado ausente do catálogo aparece numa linha
 *    "fora do catálogo" que o admin pode cadastrar (Editar → salvar cria o doc).
 */
export function PspListPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [result, setResult] = useState<PspConfigListResult>({ kind: 'LOADING' });
  const [editor, setEditor] = useState<EditorState>({ mode: 'closed' });
  const [toDelete, setToDelete] = useState<PspConfig | null>(null);
  const [deleting, setDeleting] = useState(false);
  // #1021 (2026-10-25 15:00 +0100): recusa da exclusão, mostrada dentro do confirm.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => subscribePspConfigs(setResult), []);

  const psps = useMemo((): PspRow[] => {
    if (result.kind !== 'READY') return [];
    const ids = new Set(result.psps.map((p) => p.id));
    // PSP integrado no código e ausente do catálogo: linha virtual (ativo,
    // porque o seletor de PSP do país só esconde o que o catálogo DESLIGA).
    const virtual: PspRow[] = [...CODE_INTEGRATED]
      .filter((id) => !ids.has(id))
      .map((id) => ({
        id,
        display_name: CODE_DEFAULTS[id]?.name ?? id,
        enabled: true,
        supported_currencies: CODE_DEFAULTS[id]?.currencies ?? [],
        supported_countries: CODE_DEFAULTS[id]?.countries ?? [],
        notes: null,
        created_at: 1,
        updated_at: 1,
        updated_by: '' as PspConfig['updated_by'],
        virtual: true,
      }));
    return [...result.psps, ...virtual].sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [result]);

  /** Hard-delete do PSP em `toDelete`; o servidor recusa se houver assinatura ativa. */
  async function confirmDelete(): Promise<void> {
    if (!toDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deletePspConfigCallable({
        id: toDelete.id,
        reason: t('admin.countries.psp.deleteReason'),
      });
      toast.success(t('admin.countries.psp.deleted'));
      setToDelete(null);
    } catch (err) {
      // Auditoria 2026-10-25 15:00 +0100 (#1021): era `toast.error`, atrás do overlay do confirm, que
      // não fecha no erro (mesmo achado do CRM, #174). Vai para o `Alert` dentro dele.
      setDeleteError(err instanceof Error ? err.message : t('admin.countries.psp.errorGeneric'));
    } finally {
      setDeleting(false);
    }
  }

  if (result.kind === 'LOADING') {
    return (
      <Box textAlign="center" padding="l">
        <Spinner size="large" />
      </Box>
    );
  }
  if (result.kind === 'ERROR') {
    return (
      <Box color="text-status-error" padding="l">
        {t('admin.countries.psp.errorLoad')}
      </Box>
    );
  }

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('admin.psp.subtitle')}>
          {t('admin.psp.title')}
        </Header>
      }
    >
    <SpaceBetween size="l">
      <Table<PspRow>
        items={psps}
        trackBy="id"
        variant="container"
        header={
          <Header
            counter={`(${String(psps.length)})`}
            actions={
              <Button
                variant="primary"
                iconName="add-plus"
                onClick={(): void => {
                  setEditor({ mode: 'create' });
                }}
              >
                {t('admin.countries.psp.add')}
              </Button>
            }
          >
            {t('admin.countries.psp.tableTitle')}
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit" padding="l">
            <SpaceBetween size="s">
              <Box variant="strong" color="inherit">
                {t('admin.countries.psp.emptyTitle')}
              </Box>
              <Button
                onClick={(): void => {
                  setEditor({ mode: 'create' });
                }}
              >
                {t('admin.countries.psp.add')}
              </Button>
            </SpaceBetween>
          </Box>
        }
        columnDefinitions={[
          {
            id: 'name',
            header: t('admin.countries.psp.columns.psp'),
            cell: (row): React.ReactNode => (
              <SpaceBetween direction="horizontal" size="xs">
                <Box variant="strong">{row.display_name}</Box>
                <Box color="text-status-inactive" fontSize="body-s">
                  {row.id}
                </Box>
              </SpaceBetween>
            ),
          },
          {
            id: 'integration',
            header: t('admin.countries.psp.columns.integration'),
            cell: (row): React.ReactNode =>
              CODE_INTEGRATED.has(row.id) ? (
                <SpaceBetween direction="horizontal" size="xs">
                  <Badge color="green">{t('admin.countries.psp.integrated')}</Badge>
                  {row.virtual && <Badge color="blue">{t('admin.countries.psp.notInCatalog')}</Badge>}
                </SpaceBetween>
              ) : (
                <Badge color="grey">{t('admin.countries.psp.catalogOnly')}</Badge>
              ),
          },
          // #756 — ativo por modo (produção / sandbox). PSP fora do catálogo
          // (linha virtual) está inativo nos dois: o país só vê o que está
          // ativo aqui.
          ...ALL_PAYMENT_MODES.map((mode) => ({
            id: `mode-${mode}`,
            header: t(`admin.psp.mode.${mode}`),
            cell: (row: PspRow): React.ReactNode =>
              !row.virtual && pspModeEnabled([row], row.id, mode) ? (
                <Badge color="green">{t('admin.countries.psp.enabled')}</Badge>
              ) : (
                <Badge color="grey">{t('admin.countries.psp.disabled')}</Badge>
              ),
          })),
          {
            id: 'currencies',
            header: t('admin.countries.psp.columns.currencies'),
            cell: (row): React.ReactNode =>
              row.supported_currencies.length > 0 ? row.supported_currencies.join(', ') : '—',
          },
          // Owner, 2026-10-01: os países do PSP também na lista (vazio = todos).
          {
            id: 'countries',
            header: t('admin.psp.columns.countries'),
            cell: (row: PspRow): React.ReactNode =>
              row.supported_countries.length > 0
                ? row.supported_countries.map((c) => `${flagEmoji(c)} ${c}`).join(', ')
                : t('admin.psp.allCountries'),
          },
          {
            id: 'actions',
            header: t('admin.countries.psp.columns.actions'),
            cell: (row): React.ReactNode => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="inline-link"
                  onClick={(): void => {
                    void navigate(`/psp/${row.id}`);
                  }}
                >
                  {row.virtual ? t('admin.countries.psp.addToCatalog') : t('admin.countries.psp.edit')}
                </Button>
                {!row.virtual && (
                  <Button
                    variant="inline-link"
                    onClick={(): void => {
                      setDeleteError(null);
                      setToDelete(row);
                    }}
                  >
                    {t('admin.countries.psp.delete')}
                  </Button>
                )}
              </SpaceBetween>
            ),
          },
        ]}
      />

      <PspEditDialog
        state={editor}
        existingIds={psps.filter((p) => !p.virtual).map((p) => p.id)}
        onClose={(): void => {
          setEditor({ mode: 'closed' });
        }}
      />

      <AdminConfirm
        visible={toDelete !== null}
        title={t('admin.countries.psp.deleteTitle')}
        confirmLabel={t('admin.countries.psp.deleteConfirm')}
        cancelLabel={t('admin.countries.psp.cancel')}
        loading={deleting}
        onConfirm={(): void => {
          void confirmDelete();
        }}
        onDismiss={(): void => {
          setToDelete(null);
        }}
      >
        <SpaceBetween size="m">
          <Box variant="p">{t('admin.countries.psp.deleteDescription', { id: toDelete?.id ?? '' })}</Box>
          {/* Auditoria 2026-10-25 15:00 +0100 (#1021): erro dentro do confirm, junto do footer (#174). */}
          {deleteError !== null && <Alert type="error">{deleteError}</Alert>}
        </SpaceBetween>
      </AdminConfirm>
    </SpaceBetween>
    </ContentLayout>
  );
}

const CURRENCY_OPTIONS = ALL_CURRENCIES.map((c) => ({ value: c, label: c }));

/**
 * Modal "Adicionar PSP" de catálogo (id slug, nome, moedas, países, notas).
 *
 * Origem: sem issue — commit 2b485856b, 2026-06-11 (Sprint 36 Fase 2b, aba PSP
 * de `/countries`); trazido para cá em f53a7df42 (#756).
 *
 * Contrato: `validate` bloqueia id fora de `^[a-z][a-z0-9_]*$` ou repetido,
 * nome vazio, país que não é ISO-2 e motivo vazio; depois `setPspConfig` sem
 * `modes` (o modo e as chaves são do `PspEditorPage`).
 *
 * Usado por: `PspListPage` ("Adicionar PSP"). O ramo `edit` não é mais aberto
 * por ninguém desde que "Configurar" passou a navegar para `/psp/:id` (#756).
 */
function PspEditDialog({
  state,
  existingIds,
  onClose,
}: {
  state: EditorState;
  existingIds: readonly string[];
  onClose: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const isEdit = state.mode === 'edit';
  const current = state.mode === 'edit' ? state.current : null;

  const [id, setId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [countries, setCountries] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = state.mode !== 'closed';

  useEffect(() => {
    if (!open) return;
    setId(current?.id ?? '');
    setDisplayName(current?.display_name ?? '');
    setEnabled(current?.enabled ?? true);
    setCurrencies(current?.supported_currencies ?? []);
    setCountries((current?.supported_countries ?? []).join(', '));
    setNotes(current?.notes ?? '');
    setReason('');
    setError(null);
  }, [open, current]);

  if (!open) return null;

  /** Países digitados (vírgula) → ISO-2 maiúsculo, sem vazios. */
  function parseCountries(): string[] {
    return countries
      .split(',')
      .map((c) => c.trim().toUpperCase())
      .filter((c) => c.length > 0);
  }

  /** Primeira mensagem de erro, ou `null`. */
  function validate(): string | null {
    if (!isEdit) {
      if (!/^[a-z][a-z0-9_]*$/.test(id)) return t('admin.countries.psp.errors.idSlug');
      if (existingIds.includes(id)) return t('admin.countries.psp.errors.idDuplicate');
    }
    if (displayName.trim().length === 0) return t('admin.countries.psp.errors.nameRequired');
    const badCountry = parseCountries().find((c) => !/^[A-Z]{2}$/.test(c));
    if (badCountry) return t('admin.countries.psp.errors.countryInvalid', { code: badCountry });
    if (reason.trim().length === 0) return t('admin.countries.psp.errors.reasonRequired');
    return null;
  }

  /** Valida e grava o PSP no catálogo. */
  async function submit(): Promise<void> {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await setPspConfigCallable({
        id,
        display_name: displayName.trim(),
        enabled,
        supported_currencies: currencies,
        supported_countries: parseCountries(),
        notes: notes.trim().length > 0 ? notes.trim() : null,
        reason: reason.trim(),
      });
      toast.success(t('admin.countries.psp.saved'));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.countries.psp.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): com a ação em voo, Esc/X/clique fora
      // fechavam o modal e a falha caía no `Alert` dele, já fechado — ninguém a via.
      onDismiss={(): void => { if (!(submitting)) { onClose(); } }}
      header={
        isEdit
          ? t('admin.countries.psp.editTitle', { name: current?.display_name ?? '' })
          : t('admin.countries.psp.addTitle')
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={submitting}>
              {t('admin.countries.psp.cancel')}
            </Button>
            <Button
              variant="primary"
              loading={submitting}
              onClick={(): void => {
                void submit();
              }}
            >
              {t('admin.countries.psp.save')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Box color="text-status-error" variant="p">
            {error}
          </Box>
        )}
        {!isEdit && (
          <FormField
            label={t('admin.countries.psp.idLabel')}
            description={t('admin.countries.psp.idHint')}
          >
            <Input
              value={id}
              onChange={({ detail }): void => {
                setId(detail.value.toLowerCase());
              }}
              placeholder="adyen"
            />
          </FormField>
        )}
        <FormField label={t('admin.countries.psp.nameLabel')}>
          <Input
            value={displayName}
            onChange={({ detail }): void => {
              setDisplayName(detail.value);
            }}
            placeholder="Adyen"
          />
        </FormField>
        <FormField label={t('admin.countries.psp.statusLabel')}>
          <Toggle
            checked={enabled}
            onChange={({ detail }): void => {
              setEnabled(detail.checked);
            }}
          >
            {enabled ? t('admin.countries.psp.enabled') : t('admin.countries.psp.disabled')}
          </Toggle>
        </FormField>
        <FormField label={t('admin.countries.psp.currenciesLabel')}>
          <Multiselect
            selectedOptions={CURRENCY_OPTIONS.filter((o) => currencies.includes(o.value))}
            options={CURRENCY_OPTIONS}
            placeholder={t('admin.countries.psp.currenciesPlaceholder')}
            onChange={({ detail }): void => {
              setCurrencies(detail.selectedOptions.map((o) => o.value as Currency));
            }}
          />
        </FormField>
        <FormField
          label={t('admin.countries.psp.countriesLabel')}
          description={t('admin.countries.psp.countriesHint')}
        >
          <Input
            value={countries}
            onChange={({ detail }): void => {
              setCountries(detail.value);
            }}
            placeholder="BR, IT, FR"
          />
        </FormField>
        <FormField label={t('admin.countries.psp.notesLabel')}>
          <Input
            value={notes}
            onChange={({ detail }): void => {
              setNotes(detail.value);
            }}
          />
        </FormField>
        <FormField label={t('admin.countries.psp.reasonLabel')}>
          <Input
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
            }}
            placeholder={t('admin.countries.psp.reasonPlaceholder')}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
